import { supabase } from "@/lib/supabase";
import { API } from "@/lib/api";
import {
  getGuestProfile,
  type GuestBalanceHint,
} from "@/lib/v2/guestBalance";
import { fetchGuestProfiles } from "@/lib/api/guestProfiles";
import type { Database } from "@/lib/database.types";

type Player = Database["public"]["Tables"]["players"]["Row"];

export interface GuestRsvpSummary {
  guestName: string;
  rsvpCount: number;
  lastEventDate: string | null;
}

export class GuestPromotionError extends Error {
  constructor(
    message: string,
    public code: "GUEST_NOT_FOUND" | "PLAYER_EXISTS" | "INVALID_NAME"
  ) {
    super(message);
    this.name = "GuestPromotionError";
  }
}

/** Distinct guest names from RSVPs on this group's match events. */
export async function fetchGroupGuests(groupId: string): Promise<GuestRsvpSummary[]> {
  const { data, error } = await supabase
    .from("rsvps")
    .select("guest_name, event_id, match_events!inner(group_id, event_date)")
    .eq("match_events.group_id", groupId)
    .not("guest_name", "is", null);

  if (error) throw error;

  const byName = new Map<string, { count: number; lastEventDate: string | null }>();

  for (const row of data ?? []) {
    const name = row.guest_name?.trim();
    if (!name) continue;

    const eventDate =
      (row.match_events as { event_date?: string } | null)?.event_date ?? null;
    const existing = byName.get(name);

    if (!existing) {
      byName.set(name, { count: 1, lastEventDate: eventDate });
      continue;
    }

    existing.count += 1;
    if (
      eventDate &&
      (!existing.lastEventDate || eventDate > existing.lastEventDate)
    ) {
      existing.lastEventDate = eventDate;
    }
  }

  return [...byName.entries()]
    .map(([guestName, meta]) => ({
      guestName,
      rsvpCount: meta.count,
      lastEventDate: meta.lastEventDate,
    }))
    .sort((a, b) => a.guestName.localeCompare(b.guestName));
}

async function fetchGroupEventIds(groupId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from("match_events")
    .select("id")
    .eq("group_id", groupId);

  if (error) throw error;
  return (data ?? []).map((row) => row.id);
}

/**
 * Create a players row for a recurring guest and relink their RSVPs
 * (player_id set, guest_name cleared) across all group events.
 */
export async function promoteGuestToPlayer(
  groupId: string,
  guestName: string,
  options?: { skills?: number; effort?: number; stamina?: number }
): Promise<Player> {
  const trimmed = guestName.trim();
  if (!trimmed) {
    throw new GuestPromotionError("Guest name is required", "INVALID_NAME");
  }

  const eventIds = await fetchGroupEventIds(groupId);
  if (eventIds.length === 0) {
    throw new GuestPromotionError("Guest not found", "GUEST_NOT_FOUND");
  }

  const { data: guestRsvps, error: guestError } = await supabase
    .from("rsvps")
    .select("id, event_id")
    .in("event_id", eventIds)
    .eq("guest_name", trimmed);

  if (guestError) throw guestError;
  if (!guestRsvps?.length) {
    throw new GuestPromotionError("Guest not found", "GUEST_NOT_FOUND");
  }

  const { data: existingPlayer, error: existingError } = await supabase
    .from("players")
    .select("id")
    .eq("group_id", groupId)
    .ilike("name", trimmed)
    .maybeSingle();

  if (existingError) throw existingError;
  if (existingPlayer) {
    throw new GuestPromotionError("A player with this name already exists", "PLAYER_EXISTS");
  }

  const roster = await API.getPlayers(groupId);
  const guestCache = await fetchGuestProfiles(groupId);
  const cached = getGuestProfile(guestCache, trimmed);
  const avgSkills = roster.length
    ? Math.round(roster.reduce((s, p) => s + p.skills, 0) / roster.length)
    : 75;
  const avgEffort = roster.length
    ? Math.round(roster.reduce((s, p) => s + p.effort, 0) / roster.length)
    : 75;
  const avgStamina = roster.length
    ? Math.round(roster.reduce((s, p) => s + p.stamina, 0) / roster.length)
    : 75;

  const seedScore = cached?.balanceScore ?? avgSkills;
  const skills = options?.skills ?? seedScore;
  const effort = options?.effort ?? (cached ? seedScore : avgEffort);
  const stamina = options?.stamina ?? (cached ? seedScore : avgStamina);

  const player = await API.createPlayer({
    name: trimmed,
    skills,
    effort,
    stamina,
    group_id: groupId,
  });

  for (const guestRsvp of guestRsvps) {
    const { data: playerRsvp, error: playerRsvpError } = await supabase
      .from("rsvps")
      .select("id")
      .eq("event_id", guestRsvp.event_id)
      .eq("player_id", player.id)
      .maybeSingle();

    if (playerRsvpError) throw playerRsvpError;

    if (playerRsvp) {
      const { error: deleteError } = await supabase
        .from("rsvps")
        .delete()
        .eq("id", guestRsvp.id);
      if (deleteError) throw deleteError;
      continue;
    }

    const { error: updateError } = await supabase
      .from("rsvps")
      .update({ player_id: player.id, guest_name: null })
      .eq("id", guestRsvp.id);

    if (updateError) throw updateError;
  }

  return player;
}
